import React from "react";
import ActionGroup from "../components/ActionGroup";
import EditorialContentSection from "../components/EditorialContentSection";
import FaqList from "../components/FaqList";
import SiteLayout from "../components/SiteLayout";
import {
  greyEnhancementConsultationOptions,
  greyEnhancementFaqs,
} from "../siteContent";

const greyEnhancementActions = [
  { label: "Book Consultation", to: "/contact", variant: "solid" },
];

const greyEnhancementIntroduction = {
  closingVariant: "dark",
  eyebrow: "Introduction",
  feature: {
    title: "You have already embraced your grey",
    text: "Now it is about making it feel completely you. The Grey Enhancement is for clients who are already naturally grey, silver or white, or who have completely grown out their previous colour.",
  },
  cards: [
    {
      title: "Enhance what is naturally there",
      text: "Rather than covering your grey or starting a new cycle of regular colouring, Lizzie works with your individual grey pattern using carefully considered colour placement and tones chosen specifically for you.",
    },
    {
      title: "Colour that complements the whole of you",
      text: "Your experience includes personalised colour analysis and a home-care starter collection, allowing your hair colour, natural colouring and day to day confidence to work together.",
    },
    {
      title: "Designed with freedom in mind",
      text: "The Grey Enhancement is a low-maintenance colour service, with future colour appointments designed to be spaced between 6-12 months.",
    },
  ],
  closing: (
    <>
      <h3>Beautiful, personalised colour that works with your grey</h3>
      <p>
        The goal is dimension, softness, brightness and balance without taking
        you back into a high-maintenance relationship with colour.
      </p>
    </>
  ),
  strapline: "Grey Blending | Colour Analysis | Confidence-Led Colour",
  title: "Your natural grey, beautifully enhanced.",
};

const greyEnhancementExperience = [
  {
    stage: "Step 1",
    title: "Consultation and plan",
    description:
      "We assess your current grey pattern, hair history, condition, lifestyle and goals so your enhancement is planned with clarity before your appointment.",
  },
  {
    stage: "Step 2",
    title: "Bespoke colour placement",
    description:
      "Your appointment may include highlights, lowlights, tonal work, glossing or a combination of techniques chosen to enhance your natural pattern.",
  },
  {
    stage: "Step 3",
    title: "Colour analysis and support",
    description:
      "Your seasonal colour analysis helps guide the tones used in your hair, while your colour fan and home-care starter collection support the result beyond the salon.",
  },
  {
    stage: "Step 4",
    title: "Longer refresh rhythm",
    description:
      "From six months onwards, Lizzie can reassess how your natural grey and enhancement have evolved and refresh only what is needed.",
  },
];

const greyEnhancementInvestmentCards = [
  {
    eyebrow: "Suitable for",
    number: "1",
    title: "Natural or fully grown out grey",
    text: "This service is for clients whose previous colour has fully grown out, or whose hair is already naturally grey, silver or white.",
  },
  {
    eyebrow: "Service focus",
    number: "2",
    title: "Dimension, softness and brightness",
    text: "The appointment enhances what you already have, creating balance without covering your natural grey or starting a high-maintenance colour cycle.",
  },
  {
    eyebrow: "Included",
    number: "3",
    title: "A complete enhancement experience",
    text: "Your appointment includes bespoke colour work, colour analysis, cut and finish, treatment, and your one-off personalised home-care starter collection.",
  },
  {
    eyebrow: "Investment",
    number: "4",
    title: "The Grey Enhancement",
    text: "The Grey Enhancement is an investment of £650. One appointment, personalised to you, designed for 6-12 month maintenance.",
  },
];

const greyEnhancementPaymentSchedule = [
  {
    amount: "£650",
    label: "Full investment",
    description: "Covers your complete Grey Enhancement appointment.",
  },
  {
    amount: "£200",
    label: "Online consultation booking payment",
    description:
      "Paid after your online consultation to secure your Enhancement appointment.",
  },
  {
    amount: "£275",
    label: "In person consultation booking payment",
    description:
      "Paid after an in person consultation to secure your Enhancement appointment.",
  },
  {
    amount: "£450",
    label: "Payable on the day",
    description: "The remaining balance is payable at your appointment.",
  },
];

const greyEnhancementPolicies = [
  {
    title: "Booking payment",
    text: "A non-refundable booking payment is required to book your Enhancement Experience. This is £200 after an online consultation or £275 after an in person consultation.",
  },
  {
    title: "Rescheduling",
    text: "Appointments can be rescheduled with a minimum of 72 hours' notice. Appointment movement is limited because the time needed for the Enhancement Experience has limited availability.",
  },
  {
    title: "Late changes",
    text: "Changes made within 72 hours, missed appointments, or failure to attend will result in loss of the associated payment.",
  },
  {
    title: "Payments",
    text: "All payments are non-refundable and non-transferable, as appointment times are reserved specifically for you. Payment is accepted by cash or bank transfer.",
  },
];

const GreyEnhancement = () => {
  return (
    <SiteLayout>
      <section className="page-hero page-hero--grey-transition page-hero--grey-enhancement">
        <div className="container page-hero__grid grey-transition-hero grey-enhancement-hero">
          <div className="grey-transition-hero__copy">
            <h1>The Grey Enhancement</h1>
            <p className="lead">
              A low-maintenance colour experience for women who already embrace
              their natural grey, silver or white hair and want it to feel more
              dimensional, polished and completely personal.
            </p>
            <ActionGroup
              actions={greyEnhancementActions}
              className="grey-transition-hero__actions"
            />
          </div>

          <figure className="grey-transition-hero__image grey-enhancement-hero__image">
            <img
              alt="Enhanced grey hair by Lizzie Fox Colour Studio"
              src="/clientMedia/ge1.jpeg"
            />
          </figure>
        </div>
      </section>

      <EditorialContentSection
        {...greyEnhancementIntroduction}
        className="grey-enhancement-introduction-section"
        id="grey-enhancement-introduction-title"
      />

      <section className="section section--tinted">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Experience</p>
            <h2>Your enhancement appointment</h2>
          </div>

          <div className="stage-grid">
            {greyEnhancementExperience.map((stage) => (
              <article className="stage-card" key={stage.title}>
                <p className="stage-card__label">{stage.stage}</p>
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section consultation-section consultation-section--enhancement">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Consultations</p>
            <h2>Start with a personalised enhancement plan</h2>
          </div>

          <div className="consultation-options-grid">
            {greyEnhancementConsultationOptions.map((option) => (
              <article
                className={`consultation-card consultation-card--${option.badge
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                key={option.title}
              >
                <div className="consultation-card__header">
                  <p className="consultation-card__badge">{option.badge}</p>
                  <h3>{option.title}</h3>
                </div>

                <p className="consultation-card__description">
                  {option.description}
                </p>

                <div className="consultation-card__includes">
                  <p>Includes</p>
                  <ul>
                    {option.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="consultation-card__investment">
                  <p>Investment</p>
                  <strong>{option.investment.amount}</strong>
                  {option.investment.note && (
                    <span>{option.investment.note}</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section investment-section">
        <div className="container transition-investment">
          <div className="section-heading transition-investment__heading">
            <p className="section-eyebrow">Investment</p>
            <h2>What happens next</h2>
          </div>

          <div className="transition-investment__grid">
            {greyEnhancementInvestmentCards.map((card) => (
              <article className="transition-investment__card" key={card.title}>
                <div className="transition-investment__card-header">
                  <p className="transition-investment__label">{card.eyebrow}</p>
                  <span className="transition-investment__number">
                    {card.number}
                  </span>
                </div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>

          <article className="transition-investment__schedule">
            <p className="transition-investment__label">Payment journey</p>
            <h3>Reserved after consultation, completed in one appointment</h3>
            <p className="transition-investment__intro">
              This service is specifically for clients looking for a
              low-maintenance colour relationship. Lizzie is unable to
              accommodate new clients who require colour appointments more
              frequently than every six months.
            </p>
            <div className="transition-investment__payments transition-investment__payments--four">
              {greyEnhancementPaymentSchedule.map((payment) => (
                <div
                  className="transition-investment__payment"
                  key={payment.label}
                >
                  <strong>{payment.amount}</strong>
                  <div>
                    <h4>{payment.label}</h4>
                    <p>{payment.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="section grey-enhancement-note-section">
        <div className="container grey-enhancement-note">
          <p className="section-eyebrow">A note from Lizzie</p>
          <h2>Sometimes grey just needs a little something</h2>
          <div className="grey-enhancement-note__copy">
            <p>
              Embracing your natural grey does not mean you have to simply leave
              it alone. Sometimes it just needs a little something to make it
              feel more you.
            </p>
            <p>
              I created The Grey Enhancement for women who have already embraced
              their natural colour but would love to make it feel more
              intentional, flattering and beautifully personalised without
              putting themselves back into a cycle of regular colour
              appointments.
            </p>
            <p>
              For me, it is always about working with what you naturally have,
              not against it. Enhancing the tones you love, adding softness or
              dimension where it is needed, and creating something that still
              feels completely like you.
            </p>
          </div>
          <ActionGroup
            actions={[
              {
                label: "Book Consultation",
                to: "/contact",
                variant: "solid",
              },
            ]}
            className="grey-enhancement-note__actions"
          />
        </div>
      </section>

      <section className="section section--tinted">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Booking Notes</p>
            <h2>Your appointment policy</h2>
          </div>

          <div className="card-grid card-grid--two">
            {greyEnhancementPolicies.map((policy) => (
              <article className="detail-card" key={policy.title}>
                <h3>{policy.title}</h3>
                <p>{policy.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">FAQs</p>
            <h2>Your grey enhancement, answered</h2>
          </div>
          <FaqList items={greyEnhancementFaqs} />
        </div>
      </section>
    </SiteLayout>
  );
};

export default GreyEnhancement;
